import React, { useMemo } from "react";

import { ColumnType } from "antd/es/table";
import Tag from "antd/es/tag";
import { defineMessages, IntlShape } from "react-intl";

import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { userDisplayNameComparator } from "@mapmycustomers/shared/util/comparator";
import { AvatarWithName, Button } from "@mapmycustomers/ui";

import Chip from "@app/component/Chip";
import UserPreviewCard from "@app/component/UserPreviewCard";
import UserGuidesList from "@app/scene/onboarding/components/OnboardingUsers/Users/UserGuidesList";
import UserWithStatus from "@app/scene/onboarding/types/UserWithStatus";

import styles from "./useColumns.module.scss";
import UserGuidesCompletion from "./UserGuidesCompletion";

const messages = defineMessages({
  assign: {
    id: "onboarding.users.table.column.assignGuide.button",
    defaultMessage: "Assign",
    description: "Button label in the Assign Guide column on Users Guides page",
  },
  assignGuide: {
    id: "onboarding.users.table.column.assignGuide",
    defaultMessage: "Assign Guide",
    description: "Name of a Assign Guide column on Users Guides page",
  },
  guideCompletion: {
    id: "onboarding.users.table.column.guideCompletion",
    defaultMessage: "Guide Completion",
    description: "Name of a Guide Completion column on Users Guides page",
  },
  guides: {
    id: "onboarding.users.table.column.guides",
    defaultMessage: "Guides",
    description: "Name of a Guides column on Users Guides page",
  },
  name: {
    id: "onboarding.users.table.column.name",
    defaultMessage: "User",
    description: "Name of a User column on Users Guides page",
  },
  role: {
    id: "onboarding.users.table.column.role",
    defaultMessage: "Role",
    description: "Name of a Role column on Users Guides page",
  },
  team: {
    id: "onboarding.users.table.column.team",
    defaultMessage: "Team",
    description: "Name of a Team column on Users Guides page",
  },
});

const useColumns = (intl: IntlShape, onAssignClick: (user: User) => void) =>
  useMemo<ColumnType<UserWithStatus>[]>(() => {
    return [
      {
        dataIndex: "name",
        render: (_, user: UserWithStatus) => (
          <UserPreviewCard userId={user.id}>
            <div>
              <Chip className={styles.userChip} inline>
                <AvatarWithName actionOnHover size="s" user={user} />
              </Chip>
            </div>
          </UserPreviewCard>
        ),
        sorter: (a, b) => userDisplayNameComparator(a, b),
        title: intl.formatMessage(messages.name),
        width: "100px",
      },
      {
        dataIndex: "teams",
        render: (teams: Team[]) => (
          <div className={styles.teams}>
            {teams.map((team) => (
              <Chip className={styles.teamChip} key={team.id}>
                {team.name}
              </Chip>
            ))}
          </div>
        ),
        title: intl.formatMessage(messages.team),
        width: "100px",
      },
      {
        dataIndex: "role",
        render: (role: Role) => <Tag>{role.name}</Tag>,
        title: intl.formatMessage(messages.role),
        width: "75px",
      },
      {
        dataIndex: "guides",
        render: (_, data: UserWithStatus) => <UserGuidesList statuses={data.statuses} />,
        title: intl.formatMessage(messages.guides),
        width: "200px",
      },
      {
        dataIndex: "guideCompletion",
        render: (_, data: UserWithStatus) => <UserGuidesCompletion statuses={data.statuses} />,
        title: intl.formatMessage(messages.guideCompletion),
        width: "100px",
      },
      {
        dataIndex: "assign",
        render: (_, user: User) => (
          <Button onClick={() => onAssignClick(user)} type="text">
            {intl.formatMessage(messages.assign)}
          </Button>
        ),
        title: intl.formatMessage(messages.assignGuide),
        width: "100px",
      },
    ];
  }, [intl, onAssignClick]);

export default useColumns;
